import { useLingui } from "@lingui/react/macro";

import {
  BlockContentValue,
  ButtonVariant,
  PaginationButton,
  PartialSanityImageType
} from "@silversea-ssc/ui-components-react";
import React, { PropsWithChildren, useCallback, useState } from "react";
import { EffectCoverflow, SwiperOptions } from "swiper";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import * as styles from "./SectionSlider.module.scss";
import * as cardStyles from "./SectionSliderCard.module.scss";
import SectionSliderCard from "./SectionSliderCard";
import SectionSliderCaption from "./SectionSliderCaption";
import { sliderLabels } from "components/Slider";

export type SectionSliderItem = {
  _key?: string;
  title?: string;
  subtitle?: string;
  description?: BlockContentValue;
  image?: PartialSanityImageType;
  url?: string;
  trackingName?: string;
  destinationWebCode?: string; //@deprecated
};

type Props = {
  items: SectionSliderItem[];

  cardButtonVariant?: ButtonVariant;
  cardButtonLabel?: string;
};

const swiperConfigNormal: Partial<SwiperOptions> = {
  loop: true,
  //speed: 1000,
  virtual: false,
  grabCursor: true,
  centeredSlides: true,

  effect: "coverflow",

  coverflowEffect: {
    rotate: 0, // Slide rotate in degrees
    depth: 100, // Depth offset in px (slides translate in Z axis)
    modifier: 5, // Effect multipler
    slideShadows: true,

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore // wrong library type definition
    stretch: "-4%" // Stretch space between slides (in px)
  },

  slidesPerView: 1,
  loopedSlides: 2,
  spaceBetween: 8,

  breakpoints: {
    //MD
    768: {
      slidesPerView: 1,
      spaceBetween: 24
    },
    //LG
    1024: {
      slidesPerView: 1.5,
      spaceBetween: 24
    },
    //XL
    1280: {
      slidesPerView: 1.5,
      spaceBetween: 48
    }
  }
};

const SectionSlider: React.FC<PropsWithChildren<Props>> = ({
  items,
  cardButtonVariant,
  cardButtonLabel
}) => {
  const { t } = useLingui();
  const labels = sliderLabels(t);
  const [swiper, setSwiper] = useState<SwiperRef["swiper"]>();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const prevSlide = useCallback(() => {
    if (swiper) swiper.slidePrev();
  }, [swiper]);

  const nextSlide = useCallback(() => {
    if (swiper) swiper.slideNext();
  }, [swiper]);

  const internalOnSlideChange = useCallback((e) => {
    setCurrentSlideIndex(e.realIndex);
  }, []);

  return (
    <div>
      <div className={styles.carousel}>
        <div className={styles.mask} suppressHydrationWarning>
          <Swiper
            {...swiperConfigNormal}
            modules={[EffectCoverflow]}
            slideToClickedSlide
            uniqueNavElements={false}
            passiveListeners
            onSlideChange={internalOnSlideChange}
            onSwiper={setSwiper}
          >
            {items.map(({ _key, image, trackingName, url, title }) => {
              return (
                <SwiperSlide key={_key} className={cardStyles.card}>
                  {({ isActive }) => (
                    <SectionSliderCard
                      image={image}
                      alt={title}
                      trackingName={trackingName}
                      url={isActive ? url : undefined}
                    />
                  )}
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
        <div className={styles.controls}>
          <div className={styles.totalPages}>
            {currentSlideIndex + 1} {labels.counterSeparator} {items.length}
          </div>
          <PaginationButton
            className={styles.prevButton}
            dataContentPiece="slider_previous_button"
            arrow="left"
            disabled={swiper?.isBeginning}
            onClick={prevSlide}
            aria-label={t`Previous`}
            role="button"
            inverted
            variant="primary"
          />
          <PaginationButton
            className={styles.nextButton}
            dataContentPiece="slider_next_button"
            arrow="right"
            disabled={swiper?.isEnd}
            onClick={nextSlide}
            aria-label={t`Next`}
            role="button"
            inverted
            variant="primary"
          />
        </div>
      </div>
      <SectionSliderCaption
        items={items}
        currentSlideIndex={currentSlideIndex}
        cardButtonVariant={cardButtonVariant}
        cardButtonLabel={cardButtonLabel}
      ></SectionSliderCaption>
    </div>
  );
};

export default SectionSlider;
